import { AnyAction } from 'redux';
import { ActionFunctionAny } from 'redux-actions';
import { FormErrors } from 'redux-form';

import {
  getCustomCardById,
  resetCustomCard,
  testCustomCard,
} from 'components/settings/reducers/sections/customCards';
import {
  addMagentoCard,
  editMagentoCard,
  getMagentoCard,
  getMagentoCardById,
  getMagentoCards,
  removeMagentoCard,
  resetMagentoCard,
  testMagentoCard,
} from 'components/settings/reducers/sections/magentoCards';
import {
  salesforceAddCard,
  salesforceEditCard,
  salesforceGetCard,
  salesforceGetCardById,
  salesforceGetCards,
  salesforceRemoveCard,
  salesforceResetCard,
  salesforceTestCard,
} from 'components/settings/reducers/sections/salesforceCards';
import {
  addShopifyCard,
  editShopifyCard,
  getShopifyCard,
  getShopifyCardById,
  getShopifyCards,
  removeShopifyCard,
  resetShopifyCard,
  testShopifyCard,
} from 'components/settings/reducers/sections/shopifyCards';

import type { CardType, CardTypeExternal, HookParameter } from './cards.types';
import { validate as validateMagento } from './magento/magentoCards.utils';
import { validate as validateSalesforce } from './salesforce/salesforceCards.utils';
import { validate as validateShopify } from './shopify/shopifyCards.utils';

/** The base route for all `Settings > Integrations` pages */
export const integrationsBasePath = '/settings/integrations';

/** A higher number results in performance issues with the AceEditor */
export const editorMaxLines = 50;

/**
 * Represents all possible hook variables with description. This will be removed
 * once TEAMS-6653 is done. Then we will fetch this dynamically from an
 * enpoint.
 */
export const hookUrlParameters: HookParameter[] = [
  { id: 'calling_phone_number', description: 'Calling phone number' },
  { id: 'called_phone_number', description: 'Called phone number' },
  { id: 'customer_phone_number', description: 'Customer phone number' },
  { id: 'agent_phone_number', description: 'Agent phone number' },
  { id: 'widget_id', description: 'Widget ID' },
  { id: 'facebook_page_id', description: 'Facebook page ID' },
  { id: 'sending_email', description: 'Sending email' },
  { id: 'receiving_email', description: 'Receiving email' },
  { id: 'agent_email', description: 'Agent email' },
  { id: 'customer_email', description: 'Customer email' },
  {
    id: 'a_phone_number',
    description: "Caller's phone number (typical user's number).",
  },
  {
    id: 'b_phone_number',
    description: 'The receivers number (typical your organization number).',
  },
  { id: 'a_email', description: 'The senders email address.' },
  { id: 'b_email', description: "The receiver's email address." },
  { id: 'csid', description: 'The conversation id in Dixa.' },
  { id: 'customer_external_id', description: 'Customer external ID' },
];

/**
 * Not all cards support all available hook parameters. All external cards are
 * limited to these.
 */
export const externalIntegrationsParameters = [
  'a_email',
  'b_email',
  'a_phone_number',
  'b_phone_number',
];

/** Mapping external card integrations to limited parameters */
export const externalIntegrations: Record<CardTypeExternal, string[]> = {
  shopify: externalIntegrationsParameters,
  salesforce: externalIntegrationsParameters,
  magento: externalIntegrationsParameters,
};

export const templateHelpText = (
  <span>
    <p>
      The template can be writen in html and supports{' '}
      <a
        target="_blank"
        rel="noreferrer"
        href="https://shopify.github.io/liquid/"
      >
        Liquid
      </a>{' '}
      too.
    </p>
    <p>
      <strong>Be careful not to affect anything global.</strong> Use a styling
      wrapper if you add any custom styling.
    </p>
  </span>
);

/** All cards actions mapped to `cardType` identifier */
export const cardsActions: Record<
  CardType,
  Record<string, ActionFunctionAny<AnyAction>>
> = {
  custom: {
    getCardById: getCustomCardById,
    testCard: testCustomCard,
    resetCard: resetCustomCard,
  },
  shopify: {
    getCard: getShopifyCard,
    getCardById: getShopifyCardById,
    getCards: getShopifyCards,
    addCard: addShopifyCard,
    editCard: editShopifyCard,
    removeCard: removeShopifyCard,
    testCard: testShopifyCard,
    resetCard: resetShopifyCard,
  },
  salesforce: {
    getCard: salesforceGetCard,
    getCardById: salesforceGetCardById,
    getCards: salesforceGetCards,
    addCard: salesforceAddCard,
    editCard: salesforceEditCard,
    removeCard: salesforceRemoveCard,
    testCard: salesforceTestCard,
    resetCard: salesforceResetCard,
  },
  magento: {
    getCard: getMagentoCard,
    getCardById: getMagentoCardById,
    getCards: getMagentoCards,
    addCard: addMagentoCard,
    editCard: editMagentoCard,
    removeCard: removeMagentoCard,
    testCard: testMagentoCard,
    resetCard: resetMagentoCard,
  },
};

/** Form validation functions for each card type */
// Casting to `any` here to overcome problems with correlated union type
export const validate: Record<Exclude<CardType, 'custom'>, FormErrors | any> = {
  shopify: validateShopify,
  salesforce: validateSalesforce,
  magento: validateMagento,
};
